<template>
    <div class="pl-2">
        <div v-for="(node) in tree_data" :key="node.id" :data-node-id="node.id"
            class=" bg-ui-bg bg-opacity-30 text-slate-300 hover:bg-blue-500 hover:text-white" :draggable="true"
            @dragstart="onDragStart(node, $event)" @dragover="onDragOver(node, $event)" @drop="onDrop(node, $event)">
            <!-- Fixed column grid: every slot below is always rendered, empty or
                 not, so siblings at the same depth line up whatever controls or
                 status icons an individual node happens to have. Indentation is
                 depth only — one pl-2 per nesting level, from the recursion
                 below. -->
            <div class="tree-row">
                <!-- expand/collapse: reserved for leaves too -->
                <div class="tree-cell">
                    <div v-if="node.children" class="w-full text-center cursor-pointer rounded hover:bg-blue-800"
                        @click="toggleExpand(node)">{{ node.hasp_obj.expanded ? '-' : '+' }}</div>
                </div>

                <!-- visibility -->
                <div class="tree-cell">
                    <font-awesome-icon :icon="node.hasp_obj.hidden ? 'fa-regular fa-eye-slash' : 'fa-regular fa-eye'"
                        class="cursor-pointer" @click="toggleVisibility(node)"
                        :class="node.hasp_obj.hidden ? 'text-white' : 'text-gray-500'" />
                </div>

                <!-- id + status + label: one selectable/clickable region. The id
                     slot keeps its fixed width (empty for entries without an id,
                     e.g. the Page0 Overlay — no fake id is invented) so ids stay
                     aligned; the status icons are inline and are not rendered at
                     all when a node has none, so a node without them puts its
                     label straight after the id. -->
                <div class="tree-content cursor-pointer rounded-md"
                    :class="{ 'bg-blue-800 text-white': node.hasp_obj.selected }"
                    :ref="node.hasp_obj.selected ? 'selectedNode' : undefined"
                    @click="handleClick(node.hasp_obj)">
                    <div class="tree-id">
                        <div v-if="node.hasp_id !== undefined && node.hasp_id !== ''"
                            class="pl-1 pr-1 bg-white bg-opacity-20 rounded-md border border-gray-400">{{
                                node.hasp_id
                            }}</div>
                    </div>
                    <div v-if="hasStatusIcons(node)" class="tree-status">
                        <font-awesome-icon v-if="node.hasp_obj.hasHassConfig()" :icon="'fa-solid fa-house'"
                            class="cursor-pointer text-blue-400 fa-xs"
                            @mouseenter="(event) => showHassPopup(node, event)" @mouseleave="hideHassPopup"
                            @click.stop="openHassBuilder(node.hasp_obj)" />
                        <font-awesome-icon v-if="hasObjectActions(node.hasp_obj)" :icon="'fa-solid fa-bolt'"
                            class="cursor-pointer text-yellow-400 fa-xs"
                            @mouseenter="(event) => showActionPopup(node, event)" @mouseleave="hideActionPopup" />
                        <font-awesome-icon v-if="node.hasp_obj.groupid" :icon="'fa-solid fa-expand'"
                            class="cursor-pointer text-green-500 fa-xs"
                            @mouseenter="(event) => showGroupPopup(node, event)" @mouseleave="hideGroupPopup" />
                    </div>
                    <div class="tree-name truncate">
                        <span class="font-bold">{{ node.name }}</span>
                        <template v-if="editingNodeId === node.id">
                            <input ref="commentInput" v-model="commentDraft" type="text"
                                class="ml-1 min-w-0 max-w-full rounded border border-blue-300 bg-white px-1 font-mono text-gray-900 focus:border-blue-500 focus:outline-none"
                                :aria-label="`Edit comment for ${node.name}`"
                                @click.stop @keydown.stop.enter.prevent="finishCommentEdit(node)"
                                @keydown.stop.esc.prevent="cancelCommentEdit"
                                @blur="finishCommentEdit(node)" />
                        </template>
                        <span v-else-if="node.comment" class="pl-1 font-mono">{{ node.comment }}</span>
                    </div>
                </div>
                <div class="tree-cell">
                    <button v-if="canEditComment(node)" type="button"
                        class="tree-edit-button rounded text-gray-400 hover:bg-blue-800 hover:text-white focus:outline-none focus:ring-1 focus:ring-blue-300"
                        :aria-label="`Edit comment for ${node.name}`" title="Edit comment"
                        @click.stop="startCommentEdit(node)">
                        <font-awesome-icon icon="fa-solid fa-edit" />
                    </button>
                </div>
            </div>
            <tree v-if="node.children && node.hasp_obj.expanded" :tree_data="node.children"
                :preview_icon="preview_icon" />
            <!-- HASSBuilder summary, triggered by the existing Home Assistant icon. -->
            <OutlineHassTooltip v-if="hassPopupVisible && hoveredNode === node"
                :node="node.hasp_obj" :position="popupPosition" :anchor="popupAnchor" />
            <OutlineTooltip v-if="actionPopupVisible && hoveredActionNode === node"
                :position="actionPopupPosition" :anchor="actionPopupAnchor">
                <p class="text-sm font-bold mb-2">Action Details:</p>
                <table class="table-auto w-full text-left text-xs">
                    <thead>
                        <tr>
                            <th class="border-b border-gray-600 px-1">event</th>
                            <th class="border-b border-gray-600 px-1">command</th>
                            <th class="border-b border-gray-600 px-1">groupid</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in actionRows(node.hasp_obj)" :key="row.id">
                            <td class="px-2 py-1">{{ row.event }}</td>
                            <td class="px-2 py-1">{{ row.label }}</td>
                            <td class="px-2 py-1">{{ node.hasp_obj.groupid }}</td>
                        </tr>
                    </tbody>
                </table>
            </OutlineTooltip>
            <div v-if="groupPopupVisible && hoveredGroupNode === node"
                class="absolute bg-gray-800 opacity-80 border border-white rounded-xl  text-white text-sm p-2 shadow-lg z-50"
                :style="{ top: groupPopupPosition.y + 'px', left: groupPopupPosition.x + 'px' }">
                <p class="text-sm font-bold">Group Info:</p>
                <p>Group ID: {{ node.hasp_obj.groupid }}</p>
                <!-- <p>Additional Info: {{ node.hasp_obj.groupInfo }}</p> -->
            </div>
        </div>

    </div>
</template>

<script>
import PageOverlay from '../draw/PageOverlay';
import { hasConfiguredActions } from '../utils/objectActions.js';
import { getEffectiveObjectHandlers } from '../logic/pageLogic.js';
import { getCommandDefinition } from '../actions/commandRegistry.js';
import OutlineTooltip from './OutlineTooltip.vue';
import OutlineHassTooltip from './hassbuilder/automation/OutlineHassTooltip.vue';

export default {
    name: 'tree',
    components: {
        OutlineTooltip,
        OutlineHassTooltip,
    },
    inject: ['openHassBuilder', 'selectNode'],
    props: {
        tree_data: {
            type: Array,
        },
        preview_icon: {
            type: Boolean,
            default: true,
        }
    },
    data: () => {
        return {
            // selectedNode: undefined,
            candrop: false,
            globalSourceId: 0, //chrome does not support the sourceid in events 
            hassPopupVisible: false, // Controls the visibility of the popup
            hoveredNode: null, // The node currently being hovered
            popupPosition: { x: 0, y: 0 }, // Position of the popup
            popupAnchor: null, // Viewport rect of the Home Assistant icon

            groupPopupVisible: false, // Controls the visibility of the group popup
            hoveredGroupNode: null, // The node currently being hovered for group popup
            groupPopupPosition: { x: 0, y: 0 }, // Position of the group popup

            actionPopupVisible: false, // Controls the visibility of the action popup
            hoveredActionNode: null, // The node currently being hovered for action popup
            actionPopupPosition: { x: 0, y: 0 }, // Position of the action popup
            actionPopupAnchor: null, // Viewport rect of the action icon
            editingNodeId: null,
            commentDraft: '',

        };
    },
    mounted() {
        if (this.$refs['selectedNode']) {
            this.scrollSelectedNodesIntoView();
        }
    },
    updated() {
        // When selection changes externally (e.g. history navigation triggers treeKey++)
        // scroll the newly selected item into view.
        if (this.$refs['selectedNode']) {
            this.scrollSelectedNodesIntoView();
        }
    },
    methods: {
        hasObjectActions(node) {
            // Stored handlers plus the event/action nodes wired to this object.
            return Boolean(node?.getParent) && hasConfiguredActions(getEffectiveObjectHandlers(node));
        },
        // Whether the inline status zone exists at all for this node — same three
        // predicates the icons themselves use, nothing new.
        hasStatusIcons(node) {
            const obj = node?.hasp_obj;
            return Boolean(obj?.hasHassConfig?.() || this.hasObjectActions(obj) || obj?.groupid);
        },
        actionRows(node) {
            return (node?.actions ?? []).flatMap(handler =>
                (handler.actions ?? []).map(action => ({
                    id: action.id,
                    event: handler.event,
                    label: action.type === 'command'
                        ? (getCommandDefinition(action.commandId)?.label ?? action.command ?? action.commandId)
                        : action.type === 'setObjectProperty'
                            ? `Set ${action.property?.name ?? 'object property'}`
                            : action.command,
                }))
            );
        },
        toggleExpand(node) {
            node.hasp_obj.expanded = !node.hasp_obj.expanded;
        },
        handleClick(node) {
            this.selectNode?.(node);
        },
        canEditComment(node) {
            const object = node?.hasp_obj;
            return Boolean(object && typeof object.comment === 'string' && !object.no_comment);
        },
        startCommentEdit(node) {
            if (!this.canEditComment(node)) return;
            if (this.editingNodeId !== null && this.editingNodeId !== node.id) {
                this.finishCommentEdit();
            }
            this.editingNodeId = node.id;
            this.commentDraft = node.hasp_obj.comment;
            this.$nextTick(() => {
                const input = this.$refs.commentInput;
                input?.focus();
                input?.select();
            });
        },
        finishCommentEdit(node) {
            if (this.editingNodeId === null) return;
            const editingNode = node?.id === this.editingNodeId
                ? node
                : this.tree_data?.find(item => item.id === this.editingNodeId);
            if (editingNode && editingNode.hasp_obj.comment !== this.commentDraft) {
                editingNode.hasp_obj.comment = this.commentDraft;
                editingNode.hasp_obj.getLayer?.()?.layerCallback('node-comment', editingNode.hasp_obj);
            }
            this.editingNodeId = null;
            this.commentDraft = '';
        },
        cancelCommentEdit() {
            this.editingNodeId = null;
            this.commentDraft = '';
        },
        scrollSelectedNodesIntoView() {
            const selectedNodes = this.$refs['selectedNode'];
            if (selectedNodes) {
                selectedNodes.forEach(ref => {
                    ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
                });
            }
        },
        onDragStart(sourceNode, event) {
            if (sourceNode.hasp_obj.obj === 'page' || sourceNode.hasp_obj.obj === 'mtrx_btn') {
                event.preventDefault();
                return;
            }
            this.globalSourceId = sourceNode.id;
            event.dataTransfer.setData('node_id', sourceNode.id);
            event.dataTransfer.setData('node_name', sourceNode.name);
            event.dataTransfer.effectAllowed = 'all';
            event.stopPropagation();
        },
        onDragOver(targetNode, event) {
            // const sourceId = event.dataTransfer.getData('node_id');
            if (targetNode.hasp_obj.isContainer() && this.globalSourceId !== targetNode.id) {
                event.preventDefault();
                event.dataTransfer.dropEffect = 'copy';
            }
            event.stopPropagation();
        },
        onDrop(targetNode, event) {
            const sourceId = event.dataTransfer.getData('node_id');
            if (sourceId && targetNode.id !== sourceId && event.dataTransfer.getData('node_name') !== 'page') {
                event.preventDefault();
                targetNode.hasp_obj.getLayer().moveNode(sourceId, targetNode.id);
            }
            event.stopPropagation();
            this.globalSourceId = 0;
        },
        toggleVisibility(node) {
            node.hasp_obj.hidden = !node.hasp_obj.hidden;
            node.hasp_obj.visible(!node.hasp_obj.hidden);
            const layer = node.hasp_obj.getLayer?.();
            if (layer) {
                layer.batchDraw();
                layer.updateTemplateUnderlay?.();
            }
        },
        getImageSrc(node) {
            try {
                if (node.hasp_obj !== undefined) {
                    const dat = node.hasp_obj.toDataURL();
                    return dat;
                }
            } catch {
                console.error('no image')
            }
            return '';
        },
        showHassPopup(node, event) {
            const iconElement = event.target; // Get the icon element that triggered the event
            const iconRect = iconElement.getBoundingClientRect(); // Get the bounding rectangle of the icon

            this.hideActionPopup();
            this.hoveredNode = node;
            this.hassPopupVisible = true;

            const yPosition = iconRect.bottom + 5; // Offset slightly below the icon

            this.popupPosition = {
                x: iconRect.left,
                y: yPosition,
            };
            this.popupAnchor = { left: iconRect.left, right: iconRect.right };
        },
        hideHassPopup() {
            this.hassPopupVisible = false;
            this.hoveredNode = null;
            this.popupAnchor = null;
        },
        showGroupPopup(node, event) {
            const iconElement = event.target;
            const iconRect = iconElement.getBoundingClientRect();

            this.hoveredGroupNode = node;
            this.groupPopupVisible = true;

            const popupWidth = 200;
            const xPosition = iconRect.left - 100;
            const yPosition = iconRect.bottom + 5;

            this.groupPopupPosition = {
                x: xPosition,
                y: yPosition,
            };
        },
        hideGroupPopup() {
            this.groupPopupVisible = false;
            this.hoveredGroupNode = null;
        },
        showActionPopup(node, event) {
            const iconElement = event.target;
            const iconRect = iconElement.getBoundingClientRect();

            this.hideHassPopup();
            this.hoveredActionNode = node;
            this.actionPopupVisible = true;

            const yPosition = iconRect.bottom + 5;

            this.actionPopupPosition = {
                x: iconRect.left,
                y: yPosition,
            };
            this.actionPopupAnchor = { left: iconRect.left, right: iconRect.right };
        },
        hideActionPopup() {
            this.actionPopupVisible = false;
            this.hoveredActionNode = null;
            this.actionPopupAnchor = null;
        },
    }
}
</script>
<style scoped>
/* One row = indentation (from the per-level pl-2 wrapper) + these fixed
   columns. Nothing here depends on node type, children or which status icons
   exist, so every sibling's id badge and label start at the same x. */
.tree-row {
    display: grid;
    grid-template-columns: 1.125rem 1.25rem minmax(0, 1fr) 1.25rem;
    align-items: center;
    column-gap: 0.125rem;
}

.tree-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
}

.tree-content {
    display: flex;
    align-items: center;
    min-width: 0;
}

/* Fixed width, right-aligned: 1-, 2- and 3-digit ids all end at the same x, so
   what follows starts at the same x on every row. */
.tree-id {
    flex: 0 0 2.25rem;
    display: flex;
    justify-content: flex-end;
    margin-right: 0.25rem;
}

/* Inline and only rendered when the node actually has an indicator (see
   hasStatusIcons) — no reserved column, so a node without status has its label
   directly after the id. */
.tree-status {
    display: flex;
    align-items: center;
    gap: 2px;
    flex: 0 0 auto;
    margin-right: 4px;
}

.tree-name {
    min-width: 0;
}

.tree-edit-button {
    display: inline-flex;
    width: 1.125rem;
    height: 1.125rem;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
}

.truncate {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

/* Popup styling */
.absolute {
    position: absolute;
}

.bg-gray-800 {
    background-color: #2d3748;
}

.shadow-lg {
    box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
}

.z-50 {
    z-index: 50;
}
</style>
